/* Small, progressive motion layer: native scrolling, no rendering framework. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const capable = !(navigator.connection && navigator.connection.saveData);
  let cleanup = [];
  function reset() { cleanup.forEach(fn => fn()); cleanup = []; }
  function init() {
    reset();
    initFloatMotion();
    initVideos();
    if (reduced.matches || !capable || window.DiversoMotion === false) return;
    const elements = document.querySelectorAll('.ds-reveal');
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const node = entry.target;
        // Content stays visible if JavaScript is unavailable or interrupted.
        if (node.animate) node.animate([{ transform: 'translateY(15px)', opacity: .7 }, { transform: 'translateY(0)', opacity: 1 }], { duration: 540, easing: 'cubic-bezier(.2,.7,.2,1)' });
        observer.unobserve(node);
      }), { threshold: .08 });
      elements.forEach(node => observer.observe(node));
      cleanup.push(() => observer.disconnect());
    }
    initStories();
    initDepthMotion();
  }
  function initDepthMotion() {
    if (!('IntersectionObserver' in window)) return;
    const scenes = Array.from(document.querySelectorAll('[data-ds-depth]')).map(art => ({
      art, frame: art.querySelector('.ds-art-frame'), hero: art.closest('[data-ds-hero]'), visible: false
    })).filter(scene => scene.frame);
    if (!scenes.length) return;
    let raf = 0, velocity = 0, lastY = window.scrollY, lastTime = performance.now();
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const render = () => {
      raf = 0;
      if (document.hidden) { velocity = 0; return; }
      const active = scenes.filter(scene => scene.visible && !(scene.hero && scene.hero.hasAttribute('data-ds-user-paused')));
      if (!active.length) { velocity = 0; return; }
      // Read all geometry before writing transforms; no measurements from animated children.
      const boxes = active.map(scene => scene.art.getBoundingClientRect());
      const mobile = window.innerWidth < 750 ? 0.7 : 1;
      active.forEach((scene, index) => {
        const box = boxes[index];
        const progress = clamp((window.innerHeight * 0.55 - box.top - box.height / 2) / Math.max(1, window.innerHeight * 0.65, box.height), -1, 1);
        scene.frame.style.setProperty('--ds-depth-y', ((progress * 30 + velocity * 7) * mobile).toFixed(2) + 'px');
        scene.frame.style.setProperty('--ds-depth-r', ((progress * 9 - velocity * 3) * mobile).toFixed(2) + 'deg');
        scene.frame.style.setProperty('--ds-depth-rx', ((-progress * 4 + velocity * 2) * mobile).toFixed(2) + 'deg');
      });
      velocity *= 0.8;
      if (Math.abs(velocity) > 0.012) raf = requestAnimationFrame(render);
      else velocity = 0;
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(render); };
    const scroll = () => {
      const now = performance.now();
      const elapsed = Math.max(16, now - lastTime);
      velocity = clamp((window.scrollY - lastY) / elapsed / 2.2, -1, 1);
      lastY = window.scrollY;
      lastTime = now;
      schedule();
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const scene = scenes.find(item => item.art === entry.target);
        if (scene) scene.visible = entry.isIntersecting;
      });
      schedule();
    }, { rootMargin: '40px' });
    scenes.forEach(scene => observer.observe(scene.art));
    const buttons = Array.from(new Set(scenes.map(scene => scene.hero && scene.hero.querySelector('[data-ds-float-toggle]')).filter(Boolean)));
    buttons.forEach(button => button.addEventListener('click', schedule));
    window.addEventListener('scroll', scroll, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('visibilitychange', schedule);
    cleanup.push(() => {
      observer.disconnect();
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', scroll);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('visibilitychange', schedule);
      buttons.forEach(button => button.removeEventListener('click', schedule));
      scenes.forEach(scene => ['--ds-depth-y', '--ds-depth-r', '--ds-depth-rx'].forEach(name => scene.frame.style.removeProperty(name)));
    });
  }
  function initFloatMotion() {
    document.querySelectorAll('[data-ds-hero]').forEach(hero => {
      const allowed = !reduced.matches && capable && window.DiversoMotion !== false && 'IntersectionObserver' in window;
      const button = hero.querySelector('[data-ds-float-toggle]');
      const label = button && button.querySelector('[data-ds-float-label]');
      let visible = false;
      const update = () => hero.toggleAttribute('data-ds-paused', !allowed || !visible || document.hidden || hero.hasAttribute('data-ds-user-paused'));
      const syncButton = () => {
        const paused = hero.hasAttribute('data-ds-user-paused');
        if (button) { button.setAttribute('aria-pressed', String(!paused)); button.setAttribute('aria-label', paused ? 'تشغيل حركة المنتج' : 'إيقاف حركة المنتج'); }
        if (label) label.textContent = paused ? 'تشغيل الحركة' : 'إيقاف الحركة';
      };
      const toggle = () => { hero.toggleAttribute('data-ds-user-paused'); syncButton(); update(); };
      if (button) { button.hidden = !allowed; button.addEventListener('click', toggle); cleanup.push(() => button.removeEventListener('click', toggle)); }
      syncButton();
      update();
      if (!allowed) return;
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; update(); }, { threshold: 0 });
      observer.observe(hero);
      document.addEventListener('visibilitychange', update);
      cleanup.push(() => { observer.disconnect(); document.removeEventListener('visibilitychange', update); hero.setAttribute('data-ds-paused', ''); });
    });
  }
  // Video is an optional enhancement; the product image remains the fallback.
  function initVideos() {
    document.querySelectorAll('[data-ds-video]').forEach(video => {
      const scope = video.closest('[data-ds-video-scope]') || video.closest('.ds-bold-hero') || video.parentElement;
      const stage = video.closest('.ds-bold-float--primary') || video.parentElement;
      const button = scope && scope.querySelector('[data-ds-video-toggle]');
      const label = button && button.querySelector('[data-ds-video-label]');
      const lazySource = video.getAttribute('data-src');
      if (!stage || (!lazySource && !video.getAttribute('src'))) {
        if (button) { button.disabled = true; button.hidden = true; }
        return;
      }
      let near = false, explicit = null, active = false, pending = false;
      let disposed = false, failed = false, assignedSource = false, request = 0, viewportFrame = 0;
      const automatic = !reduced.matches && capable && window.DiversoMotion !== false;
      const intended = () => explicit === null ? automatic : explicit;
      const shouldPlay = () => !disposed && !failed && near && !document.hidden && intended();
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = 'none';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.removeAttribute('autoplay');
      const present = playing => {
        active = playing;
        video.classList.toggle('is-playing', playing);
        stage.classList.toggle('is-video-active', playing);
        if (button) { button.setAttribute('aria-pressed', String(playing)); button.setAttribute('aria-label', playing ? 'إيقاف حركة المنتج' : 'تشغيل حركة المنتج'); }
        if (label) label.textContent = playing ? 'إيقاف المشهد' : 'تشغيل المشهد';
      };
      const pause = () => {
        request++;
        pending = false;
        video.pause();
        present(false);
      };
      const play = () => {
        if (!shouldPlay() || pending || active) return;
        const token = ++request;
        pending = true;
        video.muted = true;
        try {
          if (!video.getAttribute('src') && lazySource) {
            video.src = lazySource;
            assignedSource = true;
            video.load();
          }
          const started = video.play();
          if (started && typeof started.then === 'function') {
            started.then(() => {
              if (token !== request || !shouldPlay()) {
                if (!shouldPlay()) video.pause();
                return;
              }
              pending = false;
              present(true);
            }).catch(() => {
              if (disposed || token !== request) return;
              pending = false;
              // A blocked autoplay must still leave an accessible manual play action.
              explicit = false;
              present(false);
            });
          }
        } catch (_) {
          pending = false;
          explicit = false;
          present(false);
        }
      };
      const evaluate = () => { if (shouldPlay()) play(); else pause(); };
      const toggle = () => {
        if (failed || disposed) return;
        if (active || pending) { explicit = false; scope.setAttribute('data-ds-user-paused', ''); pause(); }
        else { explicit = true; scope.removeAttribute('data-ds-user-paused'); play(); }
      };
      const playing = () => {
        if (!shouldPlay()) { pause(); return; }
        pending = false;
        present(true);
      };
      const paused = () => { present(false); };
      const error = () => {
        failed = true;
        scope.setAttribute('data-ds-user-paused', '');
        pause();
        if (button) { button.disabled = true; button.hidden = true; }
      };
      const visibility = () => { evaluate(); };
      video.addEventListener('playing', playing);
      video.addEventListener('pause', paused);
      video.addEventListener('error', error);
      document.addEventListener('visibilitychange', visibility);
      if (button) {
        button.disabled = false;
        button.hidden = false;
        button.addEventListener('click', toggle);
      }
      present(false);
      let observer;
      const checkViewport = () => {
        viewportFrame = 0;
        const box = video.getBoundingClientRect();
        near = box.bottom > -120 && box.top < window.innerHeight + 120;
        evaluate();
      };
      const scheduleViewport = () => {
        if (!viewportFrame) viewportFrame = requestAnimationFrame(checkViewport);
      };
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => {
          near = entries.some(entry => entry.isIntersecting);
          evaluate();
        }, { rootMargin: '120px 0px', threshold: 0 });
        observer.observe(video);
      } else {
        // Older browsers only compute visibility in response to viewport events.
        window.addEventListener('scroll', scheduleViewport, { passive: true });
        window.addEventListener('resize', scheduleViewport, { passive: true });
        checkViewport();
      }
      cleanup.push(() => {
        disposed = true;
        if (observer) observer.disconnect();
        cancelAnimationFrame(viewportFrame);
        window.removeEventListener('scroll', scheduleViewport);
        window.removeEventListener('resize', scheduleViewport);
        document.removeEventListener('visibilitychange', visibility);
        video.removeEventListener('playing', playing);
        video.removeEventListener('pause', paused);
        video.removeEventListener('error', error);
        if (button) button.removeEventListener('click', toggle);
        pause();
        if (assignedSource) { video.removeAttribute('src'); video.load(); }
      });
    });
  }
  function initStories() {
    if (!('IntersectionObserver' in window)) return;
    document.querySelectorAll('[data-ds-story]').forEach(story => {
      const sticky = story.querySelector('.ds-scroll-sticky');
      if (!sticky) return;
      let active = false, frame = 0, flow = false, stickyHeight = 0, stickyTop = 0;
      const properties = ['--story-p', '--shoe-x', '--shoe-y', '--shoe-rx', '--shoe-ry', '--shoe-rz', '--shoe-scale', '--orbit-r'];
      story.classList.add('ds-motion-enabled');
      const update = () => {
        frame = 0;
        if (!active || document.hidden) return;
        const rect = story.getBoundingClientRect();
        const viewport = window.innerHeight;
        const progress = flow ? (viewport - rect.top) / Math.max(1, rect.height + viewport) : (stickyTop - rect.top) / Math.max(1, rect.height - stickyHeight);
        const p = Math.max(0, Math.min(1, progress));
        story.style.setProperty('--story-p', p.toFixed(4));
        story.style.setProperty('--shoe-x', (-24 + p * 48).toFixed(2) + 'px');
        story.style.setProperty('--shoe-y', (28 - p * 62).toFixed(2) + 'px');
        story.style.setProperty('--shoe-rx', (12 - p * 20).toFixed(2) + 'deg');
        story.style.setProperty('--shoe-ry', (-22 + p * 38).toFixed(2) + 'deg');
        story.style.setProperty('--shoe-rz', (-12 + p * 22).toFixed(2) + 'deg');
        story.style.setProperty('--shoe-scale', (0.92 + Math.sin(p * Math.PI) * 0.13).toFixed(4));
        story.style.setProperty('--orbit-r', (p * 40).toFixed(2) + 'deg');
      };
      const schedule = () => { if (active && !frame) frame = requestAnimationFrame(update); };
      const layout = () => {
        story.classList.remove('ds-motion-flow');
        stickyTop = parseFloat(window.getComputedStyle(sticky).top) || 0;
        stickyHeight = sticky.getBoundingClientRect().height;
        const available = window.innerHeight - stickyTop;
        // Keep native flow when the full story cannot fit below the sticky header.
        flow = stickyHeight > available + 2 || sticky.scrollHeight > available + 2;
        story.classList.toggle('ds-motion-flow', flow);
        schedule();
      };
      layout();
      const observer = new IntersectionObserver(entries => {
        active = entries[0].isIntersecting;
        story.classList.toggle('ds-story-visible', active);
        if (active) schedule();
      }, { rootMargin: '80px' });
      observer.observe(story);
      let resizeObserver;
      const heading = story.querySelector('.ds-motion-heading');
      if (heading && 'ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(layout);
        resizeObserver.observe(heading);
      }
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', layout, { passive: true });
      document.addEventListener('visibilitychange', schedule);
      cleanup.push(() => {
        observer.disconnect();
        if (resizeObserver) resizeObserver.disconnect();
        cancelAnimationFrame(frame);
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', layout);
        document.removeEventListener('visibilitychange', schedule);
        story.classList.remove('ds-motion-enabled', 'ds-motion-flow', 'ds-story-visible');
        properties.forEach(name => story.style.removeProperty(name));
      });
    });
  }
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
  if (reduced.addEventListener) reduced.addEventListener('change', init);
  else if (reduced.addListener) reduced.addListener(init);
  window.addEventListener('pagehide', reset);
  window.addEventListener('pageshow', e => { if (e.persisted) init(); });
})();
